/**
 * Read/write Supabase client for the importer-backed tables.
 *
 * IMPORTANT: this used to be a raw `fetch` client that sent ONLY the anon
 * key in the Authorization header. That worked while every RLS policy
 * accepted anonymous reads. After RLS was tightened to require an
 * authenticated user, the anon-only path silently returned empty arrays
 * (no error, just []) — App.tsx's formula picker thought "No formulas
 * yet" even though the user had dozens in Supabase.
 *
 * This rewrite delegates to the same `@supabase/supabase-js` client used
 * by `./auth`, so every read/write inherits the signed-in user's JWT and
 * RLS sees the right `auth.uid()`.
 */

import { supabase } from "./auth";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

/** Fetch all rows from a Supabase table using the signed-in user's session.
 *
 * IMPORTANT: PostgREST silently caps any un-ranged select at 1000 rows —
 * no error, just a truncated array. formula_components crossed 1000 rows on
 * 2026-08-07 and every formula saved after the cap (F251 onward) came back
 * with zero components. Reads must page with .range() until a short page,
 * ordered by a stable column so pages never skip or duplicate rows.
 */
export async function selectAll<T = Record<string, unknown>>(
  table: string,
  columns = "*",
  orderCol = "id",
  eqFilters?: Record<string, string | boolean>,
): Promise<T[]> {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured");
  }
  const PAGE = 1000;
  const all: T[] = [];
  for (let from = 0; ; from += PAGE) {
    let query = supabase.from(table).select(columns);
    if (eqFilters) {
      for (const [col, val] of Object.entries(eqFilters)) {
        query = query.eq(col, val);
      }
    }
    const { data, error } = await query
      .order(orderCol, { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      throw new Error(`Supabase ${table}: ${error.message}`);
    }
    const page = (data as T[]) || [];
    all.push(...page);
    if (page.length < PAGE) break;
  }
  return all;
}

/** Update a single row by primary-key `id`. */
export async function updateRow(
  table: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured");
  }
  const { data, error } = await supabase
    .from(table)
    .update(patch)
    .eq("id", id)
    .select();
  if (error) {
    throw new Error(`Supabase ${table} update: ${error.message}`);
  }
  // Without .select(), PostgREST returns 200 with an empty body even when
  // RLS blocked the write or no row matched — surface that as an error.
  if (!data || data.length === 0) {
    throw new Error(
      `Supabase ${table} update: no row matched id=${id} (blocked by RLS or row missing)`,
    );
  }
}

/** Update a single row matched by an arbitrary column. */
export async function updateRowByColumn(
  table: string,
  column: string,
  value: string,
  patch: Record<string, unknown>,
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error("Supabase environment variables are not configured");
  }
  const { data, error } = await supabase
    .from(table)
    .update(patch)
    .eq(column, value)
    .select();
  if (error) {
    throw new Error(`Supabase ${table} update: ${error.message}`);
  }
  // Same silent-success trap as updateRow — see the comment there.
  if (!data || data.length === 0) {
    throw new Error(
      `Supabase ${table} update: no row matched ${column}=${value} (blocked by RLS or row missing)`,
    );
  }
}

export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}
